'use client'

import { AutoCompleteAddress } from '@/components/common/address/auto-complete-address'
import { AutoComplete } from '@/components/ui/auto-complete'
import { useAddressJsonQuery } from '@/hooks/react-query/account/address/use-address-json.query'
import { useAutoCompleteAddressQuery } from '@/hooks/react-query/account/address/use-autocomplete-address.query'
import { findValueInOptions } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import { useEffect, useMemo, useRef, useState } from 'react'
import { UseFormReturn, useWatch } from 'react-hook-form'
import { CampaignRegisterFormData } from './campaign-register-schema'

interface AddressData {
  city: string
  district: string
  ward: string
}

interface CampaignAddressFieldsProps {
  form: UseFormReturn<CampaignRegisterFormData>
  // Dữ liệu tỉnh/quận/phường có sẵn (vd: điền từ thông tin user đã đăng nhập).
  // Đi qua chung cơ chế tempAddress bên dưới để được match/normalize giống
  // hệt luồng gõ tay, tránh set thẳng giá trị thô không khớp options.
  initialAddress?: AddressData
  // Ghi đè label ô địa chỉ khi ngữ cảnh dùng khác "Địa chỉ nhận hàng" mặc định.
  addressLabel?: string
}

export const CampaignAddressFields = ({
  form,
  initialAddress,
  addressLabel,
}: CampaignAddressFieldsProps) => {
  const t = useTranslations('community_threads.campaign.address_fields')
  const city = useWatch({ control: form.control, name: 'city' })
  const district = useWatch({ control: form.control, name: 'district' })
  const ward = useWatch({ control: form.control, name: 'ward' })
  const address = useWatch({ control: form.control, name: 'address' })

  const [tempAddress, setTempAddress] = useState<AddressData | null>(null)
  const [isManuallySelected, setIsManuallySelected] = useState(false)

  const hasSeededInitialRef = useRef(false)
  useEffect(() => {
    if (hasSeededInitialRef.current || !initialAddress) return
    const hasAny =
      initialAddress.city || initialAddress.district || initialAddress.ward
    if (!hasAny) return
    hasSeededInitialRef.current = true
    // eslint-disable-next-line react-hooks/set-state-in-effect -- seed tempAddress once from prop (user data loads async, may arrive after mount)
    setTempAddress(initialAddress)
  }, [initialAddress])

  // Trường hợp chỉ có sẵn chuỗi địa chỉ đầy đủ (vd: user.address) mà không có
  // sẵn city/district/ward tách sẵn -> geocode chuỗi đó qua API gợi ý địa chỉ
  // (giống hệt lúc khách gõ tay) để tự suy ra tỉnh/quận/phường.
  const [shouldGeocodeAddress, setShouldGeocodeAddress] = useState(false)
  const hasTriedGeocodeRef = useRef(false)

  useEffect(() => {
    if (hasTriedGeocodeRef.current || hasSeededInitialRef.current) return
    if (!address?.trim() || city || district || ward) return
    hasTriedGeocodeRef.current = true
    // eslint-disable-next-line react-hooks/set-state-in-effect -- trigger geocode query once for a prefilled address string without structured city/district/ward
    setShouldGeocodeAddress(true)
  }, [address, city, district, ward])

  const { data: geocodeSuggestions } = useAutoCompleteAddressQuery(
    { input: address?.trim() || '', more_compound: true, limit: 1 },
    shouldGeocodeAddress
  )

  useEffect(() => {
    if (!shouldGeocodeAddress) return
    const firstSuggestion = geocodeSuggestions?.predictions?.[0]
    if (!firstSuggestion) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- consume geocode result once it arrives (async query, one-shot)
    setShouldGeocodeAddress(false)
    if (firstSuggestion.compound) {
      setTempAddress({
        city: firstSuggestion.compound.province || '',
        district: firstSuggestion.compound.district || '',
        ward: firstSuggestion.compound.commune || '',
      })
    }
  }, [shouldGeocodeAddress, geocodeSuggestions])

  // Track previous tempAddress to detect if it's fresh (from suggestion) or stale
  const prevTempAddressRef = useRef<AddressData | null>(null)

  // Cờ one-shot cho chu trình điền city→district→ward theo mỗi tempAddress.
  // Mỗi field chỉ được setValue tối đa 1 lần cho mỗi tempAddress, nên effect
  // bên dưới không thể tự kích hoạt lại chính nó thành vòng lặp (nguồn lỗi
  // "Maximum call stack size exceeded" trên production).
  const fillDoneRef = useRef({ city: true, district: true, ward: true })

  const { data: dataCity, isSuccess: cityReady } = useAddressJsonQuery()
  const { data: dataDistrict, isSuccess: districtReady } = useAddressJsonQuery(
    { city_code: city ?? tempAddress?.city },
    Boolean(city ?? tempAddress?.city)
  )
  const { data: dataWard, isSuccess: wardReady } = useAddressJsonQuery(
    {
      district_id: district ?? tempAddress?.district,
      city_code: city ?? tempAddress?.city,
    },
    Boolean((city ?? tempAddress?.city) && (district ?? tempAddress?.district))
  )

  const addressOptions = useMemo(
    () => ({
      city: dataCity?.city.map((c) => ({ label: c.city, value: c.city })) || [],
      district:
        dataDistrict?.district.map((d) => ({
          label: d.district,
          value: d.district,
        })) || [],
      ward: dataWard?.ward.map((w) => ({ label: w.ward, value: w.ward })) || [],
    }),
    [dataCity, dataDistrict, dataWard]
  )

  useEffect(() => {
    if (!tempAddress || isManuallySelected) return

    // tempAddress mới từ suggestion → reset cờ để điền lại cả 3 field.
    // tempAddress cũ (đã điền xong, cờ đều true) thì effect không làm gì,
    // nên giá trị chọn từ address book không bao giờ bị ghi đè.
    if (prevTempAddressRef.current !== tempAddress) {
      prevTempAddressRef.current = tempAddress
      fillDoneRef.current = { city: false, district: false, ward: false }
    }
    const fillDone = fillDoneRef.current

    // Điền tuần tự; sau mỗi setValue phải return để chờ query cấp dưới
    // refetch theo giá trị mới rồi effect tự chạy lại — nếu xử lý tiếp trong
    // cùng lần chạy, options trong closure vẫn là của city/district CŨ
    // (stale) nên ward sẽ không match và bị bỏ qua vĩnh viễn.
    // Chưa match thì giữ nguyên cờ để thử lại khi options mới đổ về.
    if (!fillDone.city) {
      if (!cityReady || !dataCity) return
      const cityValue = findValueInOptions(
        tempAddress.city,
        addressOptions.city
      )
      if (!cityValue) return
      fillDone.city = true
      if (form.getValues('city') !== cityValue) {
        form.setValue('city', cityValue, {
          shouldValidate: true,
          shouldDirty: true,
        })
        return
      }
    }

    if (!fillDone.district) {
      if (!districtReady || !dataDistrict) return
      const districtValue = findValueInOptions(
        tempAddress.district,
        addressOptions.district
      )
      if (!districtValue) return
      fillDone.district = true
      if (form.getValues('district') !== districtValue) {
        form.setValue('district', districtValue, {
          shouldValidate: true,
          shouldDirty: true,
        })
        return
      }
    }

    if (!fillDone.ward) {
      if (!wardReady || !dataWard) return
      const wardValue = findValueInOptions(
        tempAddress.ward,
        addressOptions.ward
      )
      if (!wardValue) return
      fillDone.ward = true
      if (form.getValues('ward') !== wardValue) {
        form.setValue('ward', wardValue, {
          shouldValidate: true,
          shouldDirty: true,
        })
      }
    }
  }, [
    tempAddress,
    cityReady,
    districtReady,
    wardReady,
    addressOptions,
    dataCity,
    dataDistrict,
    dataWard,
    form,
    isManuallySelected,
  ])

  const handleCityChange = (value: string) => {
    if (!value) return
    setIsManuallySelected(true)
    form.setValue('city', value, { shouldValidate: true, shouldDirty: true })
    form.setValue('district', '', { shouldValidate: true, shouldDirty: true })
    form.setValue('ward', '', { shouldValidate: true, shouldDirty: true })
  }

  const handleDistrictChange = (value: string) => {
    if (!value) return
    setIsManuallySelected(true)
    form.setValue('district', value, {
      shouldValidate: true,
      shouldDirty: true,
    })
    form.setValue('ward', '', { shouldValidate: true, shouldDirty: true })
  }

  const handleWardChange = (value: string) => {
    if (!value) return
    setIsManuallySelected(true)
    form.setValue('ward', value, { shouldValidate: true, shouldDirty: true })
  }

  return (
    <div className='space-y-3'>
      <AutoCompleteAddress
        value={address}
        onValueChange={(value) => {
          setIsManuallySelected(false)
          form.setValue('address', value, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }}
        onChangeAddressData={(data) => {
          // Địa chỉ do khách tự nhập (không kèm dữ liệu tỉnh/quận/phường từ API) -> reset các ô chọn
          if (!data.city && !data.district && !data.ward) {
            setTempAddress(null)
            form.setValue('city', '', {
              shouldValidate: true,
              shouldDirty: true,
            })
            form.setValue('district', '', {
              shouldValidate: true,
              shouldDirty: true,
            })
            form.setValue('ward', '', {
              shouldValidate: true,
              shouldDirty: true,
            })
            return
          }
          setTempAddress(data)
        }}
        placeholder={t('address_placeholder')}
        inputClassName='h-11'
        error={form.formState.errors.address}
        label={addressLabel ?? t('address_label')}
      />

      <div className='grid grid-cols-1 gap-3 md:grid-cols-3'>
        <AutoComplete
          label={t('city_label')}
          placeholder={t('city_placeholder')}
          options={addressOptions.city}
          value={city}
          onValueChange={handleCityChange}
          inputClassName='h-11'
          error={form.formState.errors.city}
        />
        <AutoComplete
          label={t('district_label')}
          placeholder={t('district_placeholder')}
          options={addressOptions.district}
          value={district}
          onValueChange={handleDistrictChange}
          inputClassName='h-11'
          disabled={!city}
          error={form.formState.errors.district}
        />
        <AutoComplete
          label={t('ward_label')}
          placeholder={t('ward_placeholder')}
          options={addressOptions.ward}
          value={ward}
          onValueChange={handleWardChange}
          inputClassName='h-11'
          disabled={!district}
          error={form.formState.errors.ward}
        />
      </div>
    </div>
  )
}
